/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useRef } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { CommerceExperience as CommerceExperienceIframe } from '@atlassian/commerce-experience-sdk';

import { type CommerceExperienceSdkReactProps } from './types';

const styles = cssMap({
	container: {
		height: '100%',
		width: '100%',
		overflow: 'hidden',
	},
});

/**
 * Renders a commerce experience iframe.
 *
 * Please ensure you have checked availability of the SDK in this environment before using this component
 * via the useIsAvailableSdk hook from this package.
 * @param {string} props.url The URL for the iframe.
 * @param {Function} props.onComplete Callback for completion events. Should be memoized with useCallback.
 * @param {Function} props.onError Callback for error events. Should be memoized with useCallback.
 * @param {Function} props.onLoad Callback for load events. Should be memoized with useCallback.
 * @param {Object} props.configuration Configuration object for the iframe. Changes to this object will cause the component to re-render.
 * @param {string} props.title Accessible name for the iframe (title attribute). Recommended for accessibility.
 *
 * @returns {React.ReactElement} A div element containing the commerce experience iframe.
 */
const CommerceExperience = ({
	url,
	onComplete,
	onLoad,
	onError,
	onMessage,
	configuration,
	useFullUrl,
	title,
	xcss,
	testId,
}: CommerceExperienceSdkReactProps): React.ReactElement => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const commerceExperienceIframe = useRef<CommerceExperienceIframe | null>(null);

	// We stringify the configuration in order to make sure the dependency array does not cause re-renders
	const stringifiedConfiguration = JSON.stringify(configuration || {});

	useEffect(() => {
		if (containerRef.current) {
			commerceExperienceIframe.current = new CommerceExperienceIframe(url, {
				container: containerRef.current,
				onComplete,
				onError,
				onMessage,
				onLoad,
				configuration: JSON.parse(stringifiedConfiguration),
				useFullUrl,
				title,
			});

			commerceExperienceIframe.current?.init();
		}

		return () => {
			commerceExperienceIframe.current?.cleanup();
		};
	}, [url, onComplete, onError, stringifiedConfiguration, onMessage, onLoad, useFullUrl, title]);

	return <div ref={containerRef} css={styles.container} className={xcss} data-testid={testId} />;
};

export default CommerceExperience;
